/**
 * The strings **two or more** of the four content surfaces render. A string one
 * surface owns lives in that surface's own `copy.ts`.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, and no restatement of a mechanic the screen already
 * performs. «There is no review step» is stated as *what it means for the work*
 * — what you publish is seen immediately — not as «a configuration key is
 * false».
 */
export const sharedCopy = {
  crumbs: {
    console: 'لوحة التشغيل',
    content: 'المحتوى',
  },

  action: {
    save: 'احفظ',
    cancel: 'ألغِ',
    back: 'عد إلى القائمة',
  },

  /** The line above every authoring form. It says there is a requirement, not which. */
  requiredNote: 'الحقول المعلَّمة بنجمة مطلوبة قبل النشر.',
  requiredMark: '*',

  fields: {
    title: 'العنوان',
    description: 'الوصف',
    category: 'التصنيف',
    categoryPlaceholder: 'اختر تصنيفاً…',
    tags: 'الوسوم',
    /**
     * The tag field is a picker, so it carries a picker's copy.
     *
     * It reads the same as the editorial rail's on purpose: one vocabulary
     * chosen two ways would have an operator learn the control twice.
     */
    tagsSearch: 'ابحث في الوسوم',
    tagsPlaceholder: 'ابحث في الوسوم…',
    tagsCount: (count: string) => `${count} من المفردة`,
    tagsNoResults: 'لا وسم بهذا الاسم في المفردة.',
    tagsRemove: (label: string) => `أزل وسم «${label}»`,
    access: 'الوصول',
    year: 'سنة الإنتاج',
    country: 'بلد الإنتاج',
    ageRating: 'التصنيف العمري',
    /** No setting declares the members, so the operator writes the value. */
    ageRatingHint: 'من التصنيفات التي يعرّفها نوع المحتوى. يظهر للمشاهد على صفحة العنوان.',
    ageRatingPlaceholder: 'اختر تصنيفاً عمريّاً…',
    countryHint: 'رمز الدولة من حرفين بمعيار ISO 3166-1 — مثل YE.',
    cover: 'الغلاف',
  },

  accessModes: {
    subscription: 'بالاشتراك — يفتحه اشتراك المشاهد',
    free: 'مجّانيّ — مفتوح لكلّ مشاهد مسجَّل',
    /** One subscription opens the whole catalogue, so no per-title amount is authored. */
    hint: 'اشتراكٌ واحد يفتح الفهرس كلّه، فلا سعر يُكتب لعنوانٍ مفرد. اختر «مجّانيّ» ليُشاهَد هذا العنوان بلا اشتراك.',
  },

  sections: {
    core: 'الحقول العامّة',
    metadata: 'البيانات الوصفية',
    credits: 'المنسوبون',
    provider: 'المزوّد من الخارج',
    cover: 'الغلاف',
  },

  credits: {
    add: 'أضف',
    pickPlaceholder: 'اختر شخصاً من السجلّ…',
    pickLabel: 'اختر منسوباً من السجلّ',
    rolesLabel: (name: string) => `أدوار ${name} على هذا العنوان`,
    remove: (name: string) => `أزل «${name}» من المنسوبين`,
    registryLead: 'الشخص غير موجود في السجلّ؟',
    registryLink: 'أنشئه في المنسوبين',
    registryTail: 'ثمّ عُد وأضفه هنا.',
  },

  artwork: {
    caption: 'أغراض الصور ومصدر كلّ منها',
    columns: { purpose: 'الغرض', ratio: 'النسبة', source: 'المصدر' },
    /** One upload produces four purposes; the table is what shows that. */
    note: 'المصغّرة وحدها مطلوبة؛ الباقي يُشتقّ منها بقصٍّ متناسب أو يصل من تعبئة المزوّد.',
    pick: 'اختر ملفّاً',
    /** Drawn on the zone the file was dropped into, beside its own progress bar. */
    uploading: 'جارٍ الرفع',
  },

  states: {
    loading: 'جارٍ التحميل',
  },

  /** The line beneath a list with no traversal control. Silent truncation is what is forbidden. */
  truncation: { prefix: 'معروض', middle: 'من' },
} as const;
